import { useEffect, useRef, useState } from 'react';
import { uploadContent } from 'modules/common/api';
import PropTypes from 'prop-types';
import {
  ErrorText,
  HiddenInput,
  Image,
  ImageBox,
  ImagesPreview,
  RemoveButton,
  UploadContainer,
  UploadLink,
  UploadText,
} from './FileUploader.style';

const FileUploader = ({
  initialUrls = [],
  single = false,
  changeHandler,
  operationKey,
  fileTypes = [],
}) => {
  const inputRef = useRef(null);
  const [images, setImages] = useState(initialUrls);
  const [isUploading, setIsUploading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    changeHandler(images);
  }, [images]);

  const validateFiles = files => {
    if (fileTypes.length === 0) {
      setErrorMsg('');
      return files;
    }

    const validFiles = [];
    const invalidFiles = [];

    files.forEach(file => {
      if (fileTypes.includes(file.type)) {
        validFiles.push(file);
      } else {
        invalidFiles.push(file.name);
      }
    });

    if (invalidFiles.length > 0) {
      setErrorMsg(`Unsupported file type(s): ${invalidFiles.join(', ')}`);
    } else {
      setErrorMsg('');
    }

    return validFiles;
  };

  const uploadFiles = async files => {
    const formData = new FormData();
    formData.append('OperationKey', operationKey);
    files.forEach(file => {
      formData.append('Files', file);
    });

    try {
      const response = await uploadContent(formData, {
        ServiceKey: 'servicescontent',
        'x-vzt-Component': 'VWEB',
        'Content-Type': 'multipart/form-data',
      });
      const uploadedFiles = response.data?.data || [];
      return uploadedFiles.map(file => file.fileUrl);
    } catch (error) {
      console.error('Error uploading files:', error);
      return [];
    }
  };

  const handleFiles = async rawFiles => {
    const validFiles = validateFiles(rawFiles);
    if (validFiles.length === 0) return;

    setIsUploading(true);

    if (single) {
      const firstFile = validFiles[0];
      const uploadedUrls = await uploadFiles([firstFile]);
      if (uploadedUrls.length > 0) {
        setImages([uploadedUrls[0]]);
      }
    } else {
      const uploadedUrls = await uploadFiles(validFiles);
      setImages(prev => [...prev, ...uploadedUrls]);
    }

    setIsUploading(false);
  };

  const handleFileChange = async e => {
    const files = Array.from(e.target.files || []);
    await handleFiles(files);
  };

  const handleDrop = async e => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    await handleFiles(files);
  };

  const handleClick = () => {
    inputRef.current?.click();
  };

  const removeImage = imgUrl => {
    setImages(prev => prev.filter(img => img !== imgUrl));
  };

  return (
    <div>
      <UploadContainer onDragOver={e => e.preventDefault()} onDrop={handleDrop}>
        <div>↑</div>
        <UploadText>
          {isUploading ? (
            'Uploading...'
          ) : (
            <>
              Drop here to attach or <UploadLink onClick={handleClick}>upload</UploadLink>
            </>
          )}
        </UploadText>

        <HiddenInput
          type="file"
          ref={inputRef}
          multiple={!single}
          accept={fileTypes?.join(',')}
          onChange={handleFileChange}
        />

        {errorMsg && <ErrorText>{errorMsg}</ErrorText>}

        <ImagesPreview>
          {images.map(img => (
            <ImageBox key={img}>
              <Image src={img} alt="preview" />
              <RemoveButton onClick={() => removeImage(img)}>×</RemoveButton>
            </ImageBox>
          ))}
        </ImagesPreview>
      </UploadContainer>
    </div>
  );
};

FileUploader.propTypes = {
  initialUrls: PropTypes.arrayOf(PropTypes.string),
  single: PropTypes.bool,
  changeHandler: PropTypes.func.isRequired,
  operationKey: PropTypes.string.isRequired,
  fileTypes: PropTypes.arrayOf(PropTypes.string), // e.g. ['image/png', 'image/jpeg']
};

export default FileUploader;
